import type { QuoteLineItemFieldsFragment } from "gql/api";

type PricedLineItem = Pick<
  QuoteLineItemFieldsFragment,
  | "erp_product_hierarchy"
  | "erp_price"
  | "price_override"
  | "quantity"
  | "tax_type"
>;

function getQuoteAdjustmentType(item: PricedLineItem) {
  switch (item.erp_product_hierarchy?.trim().toUpperCase()) {
    case "2610638SOTHALL_WTY": // Warranty
    case "2610638SOTHALLCRDT": // Credits
      return "credit";
    case "2610638SSUROP49041": // Discounts
      return "discount";
    default:
      return null;
  }
}

/** These ERP hierarchies represent deductions even when their prices are positive. */
export function isQuoteAdjustmentLineItem(item: PricedLineItem): boolean {
  return getQuoteAdjustmentType(item) !== null;
}

export function getQuoteLineUnitPrice(item: PricedLineItem): number {
  const override = item.price_override;
  const hasOverride = override != null && String(override).trim() !== "";
  const price = Number(hasOverride ? override : item.erp_price);
  const fallback = Number(item.erp_price);
  const finitePrice = Number.isFinite(price)
    ? price
    : Number.isFinite(fallback)
      ? fallback
      : 0;
  return isQuoteAdjustmentLineItem(item) && finitePrice !== 0
    ? -Math.abs(finitePrice)
    : finitePrice;
}

/** Credits and discounts are positive summaries; signed prices already reduce totals. */
export function getQuoteAdjustments(items: PricedLineItem[]) {
  let credits = 0;
  let discounts = 0;

  for (const item of items) {
    const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
    const price = getQuoteLineUnitPrice(item);

    if (item.tax_type === "free") {
      // A waived deduction is neither an applied credit nor a discount.
      discounts += Math.max(0, price) * quantity;
    } else if (getQuoteAdjustmentType(item) === "discount") {
      discounts += Math.abs(price) * quantity;
    } else if (getQuoteAdjustmentType(item) === "credit" || price < 0) {
      credits += Math.abs(price) * quantity;
    } else {
      discounts +=
        Math.max(0, (Number(item.erp_price) || 0) - price) * quantity;
    }
  }

  return { credits, discounts };
}
